import React from "react";
import { DatePicker } from "antd";
import type { Dayjs } from "dayjs";
import { DAY_WEEK_TYPE } from "../../utils/constants";

const { RangePicker } = DatePicker;

export type EventType = (typeof DAY_WEEK_TYPE)[keyof typeof DAY_WEEK_TYPE];

interface DateSelectorProps {
  eventType: EventType;
  dateRangeLimit?: number;
  onChange?: (dates: Dayjs | [Dayjs, Dayjs] | null) => void;
  value?: Dayjs | [Dayjs, Dayjs] | null;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  eventType,
  dateRangeLimit = 14,
  onChange,
  value = null,
}) => {
  const exceedsRangeLimit = (start: Dayjs, end: Dayjs) => {
    const daysBetween = Math.abs(
      end.startOf("day").diff(start.startOf("day"), "day")
    );

    // The range is inclusive, so its endpoints are limit - 1 days apart.
    return daysBetween >= dateRangeLimit;
  };

  // ── DAY mode ────────────────────────────────────────────────────────────────
  if (eventType === DAY_WEEK_TYPE.DAY) {
    return (
      <DatePicker
        value={value as Dayjs | null}
        onChange={(date) => onChange?.(date)}
        allowClear
        format="DD MMM YYYY"
        placeholder="Select date"
        style={{ borderRadius: 8, minWidth: 160 }}
        size="small"
      />
    );
  }

  // ── WEEK mode ────────────────────────────────────────────────────────────────
  return (
    <RangePicker
      value={value as [Dayjs, Dayjs] | null}
      onChange={(dates) => {
        if (!dates?.[0] || !dates?.[1]) {
          onChange?.(null);
          return;
        }

        if (exceedsRangeLimit(dates[0], dates[1])) {
          return;
        }

        onChange?.([dates[0], dates[1]]);
      }}
      disabledDate={(current, { from }) =>
        Boolean(from && exceedsRangeLimit(from, current))
      }
      allowClear
      format="DD MMM YYYY"
      placeholder={["Start date", "End date"]}
      style={{ borderRadius: 8 }}
      size="large"
    />
  );
};

export default DateSelector;
